import { Injectable } from '@angular/core';
import { SetRole } from '@app/finxone-web-frontend/app/lib/actions/role.action';
import { ActiveConfig, ActiveState, SupportedDevice } from '@finxone-platform/shared/sys-config-types';
import { removeSpaceFromRole } from '@finxone-platform/shared/workflow-mapping';
import { Store } from '@ngxs/store';
import { HlmTabsService } from 'libs/ui/ui-tabs-helm/src/lib/hlm-tabs.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, Subject, firstValueFrom, take, tap } from 'rxjs';
import { defaultActiveConfig } from '../active-config/active-config.type';

@Injectable({
  providedIn: 'root',
})
export class ActiveConfigService {
  updateTabSelection() {
    Object.entries(this.activeConfig.getValue()?.activeTabSelections ?? {}).forEach(([key, value]) => {
      this.hlmTabService.setActiveTab(key, value);
    });
  }
  dropIndex = new Subject<number>();
  public isAppWindowPreview = false;
  private activeConfig = new BehaviorSubject(defaultActiveConfig);

  public activeSessionsCount = 0;
  public activeSessionUsers: {
    id: string;
    clientId: string;
    firstName: string;
    lastName: string;
    email: string;
  }[] = [];

  updateActiveState = new Subject<ActiveState>();
  droppedIndex = 0;

  constructor(
    private store: Store,
    private deviceDetectorService: DeviceDetectorService,
    private hlmTabService: HlmTabsService,
  ) {
    this.activeConfig.next({
      ...this.getDataFromLocalStorage(),
      isAppWindowPreview: this.isAppWindowPreview,
    });

    Object.entries(this.activeConfig.getValue()?.activeTabSelections ?? {}).forEach(([key, value]) => {
      this.hlmTabService.setActiveTab(key, value);
    });

    this.hlmTabService.activeTabUpdates.subscribe(([key, value]) => {
      this.updateConfig((config) => {
        if (config.activeTabSelections) config.activeTabSelections[key] = value;

        return config;
      });
    });
  }

  private getDataFromLocalStorage(): ActiveConfig {
    if (localStorage.getItem('activeConfig') !== null) {
      const active = JSON.parse(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        localStorage.getItem('activeConfig')!,
      ) as ActiveConfig;

      return {
        ...active,
        activeTabSelections: {},
      };
    } else {
      return defaultActiveConfig;
    }
  }

  storeConfig(config: ActiveConfig) {
    config['isAppWindowPreview'] = undefined;
    localStorage.setItem('activeConfig', JSON.stringify(config));
    // because the localStorage events do not trigger in the same window that made the changes. This forces all listeners to react to the update.
    window.dispatchEvent(new Event('storage'));
  }

  getConfig(): Observable<ActiveConfig> {
    return this.activeConfig;
  }

  async updatedActiveConfig() {
    return await firstValueFrom(this.getConfig());
  }

  updateConfig(fn: (config: ActiveConfig) => ActiveConfig) {
    this.getConfig()
      .pipe(
        take(1),
        tap((config) => {
          const after = fn(config);

          after['isAppWindowPreview'] = undefined;

          this.storeConfig(after);

          const deviceInfo = this.deviceDetectorService.getDeviceInfo();
          this.store.dispatch(
            new SetRole(
              removeSpaceFromRole(after.activeRole),
              after.activeDevice ?? (deviceInfo.deviceType as SupportedDevice),
            ),
          );
          this.activeConfig.next({
            ...after,
            isAppWindowPreview: this.isAppWindowPreview,
          });
        }),
      )
      .subscribe();
  }

  setGridDropIndex(index: number) {
    this.dropIndex.next(index);
    this.droppedIndex = index;
  }

  getGridDropIndex(): Observable<number> {
    return this.dropIndex;
  }

  get activeState() {
    let activeState!: ActiveConfig;
    this.getConfig()
      .pipe(take(1))
      .subscribe((activeConfig) => {
        activeState = activeConfig;
      });
    return activeState;
  }

  setActiveRole(role: string): void {
    try {
      const deviceInfo = this.deviceDetectorService.getDeviceInfo();
      this.store.dispatch(
        new SetRole(role, this.activeState.activeDevice ?? (deviceInfo.deviceType as SupportedDevice)),
      );
    } catch (error) {
      console.error('error:setActiveRole', error);
      return;
    }
  }
}
